import { useEffect } from 'react';

import {
  useBoardEditingStore,
  useBoardSelectionStore,
  useBoardViewportStore,
  useWallContentStore,
  useWallParticipantsStore,
  useWorkspaceUiStore,
  type WallBoard,
} from '@walls/store';
import { findParentClusterByCardId } from '../card-actions/utils/cardLookup';
import { getCreateCardInitialText, shouldStartCreateCardFromKey } from './keyboardShortcuts';

export function useBoardKeyboardShortcuts(board: WallBoard) {
  const editingMode = useBoardEditingStore((state) => state.editingMode);
  const startCreateCard = useBoardEditingStore((state) => state.startCreateCard);
  const currentMousePosition = useBoardViewportStore((state) => state.currentMousePosition);
  const setDropPosition = useBoardViewportStore((state) => state.setDropPosition);
  const cards = useWallContentStore((state) => state.cards);
  const currentElement = useBoardSelectionStore((state) => state.currentElement);
  const currentParticipantId = useWallParticipantsStore((state) => state.currentParticipantId);
  const selectedTool = useWorkspaceUiStore((state) => state.selectedTool);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const canCreate = Boolean(
        board.isOwner || (currentParticipantId && (board.isMember || board.canCollaborate))
      );

      if (
        !shouldStartCreateCardFromKey({
          allowCardCreation: board.allowCardCreation,
          canCreate,
          editing: editingMode !== null,
          event,
          selectedTool,
        })
      ) {
        return;
      }

      const initialText = getCreateCardInitialText(event) ?? '';

      if (currentMousePosition.x >= 0 && currentMousePosition.y >= 0) {
        setDropPosition(currentMousePosition);
      }

      event.preventDefault();
      const targetCluster =
        currentElement?.type === 'card'
          ? findParentClusterByCardId(cards, currentElement.id)
          : null;

      startCreateCard(initialText, targetCluster?.id ?? null);
    };

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [
    board.allowCardCreation,
    board.canCollaborate,
    board.isMember,
    board.isOwner,
    cards,
    currentElement,
    currentMousePosition,
    currentParticipantId,
    editingMode,
    selectedTool,
    setDropPosition,
    startCreateCard,
  ]);
}
